import React from 'react';
import { TFunction } from 'i18next';
import { CellContext } from '@tanstack/react-table';
import { ColumnConfig } from '@/types/table.types';
import { DeviceDashboardTableData } from '@/types/operational-ui.types';
import { formatVehicleTypeForDisplay } from '@/config/deviceTypes.config';
import i18n from '@/i18n/i18n';
import {
  DeviceDashboardStatusCell,
  DeviceDashboardLastActivityCell,
  DeviceDashboardLastOperationCell,
  DeviceDashboardLastOperationTzCell,
  DeviceDashboardLastConnectionTzCell,
} from '@/components/features/operations/deviceDashboardTableCells';
import { DeviceDashboardVehicleTypeCell } from '@/components/features/operations/deviceDashboardTableCells/DeviceDashboardVehicleTypeCell';

export type DeviceDashboardColumnConfig = ColumnConfig<DeviceDashboardTableData>;

export const getDeviceDashboardTableColumns = (t: TFunction): DeviceDashboardColumnConfig[] => [
  {
    id: 'serialNumber',
    accessor: 'serialNumber',
    header: 'pages.operations.devices.columns.serialNumber',
    enableSorting: true,
    enableFilter: true,
    filterType: 'select',
  },
  {
    id: 'plate',
    accessor: 'plate',
    header: 'pages.operations.devices.columns.plate',
    enableSorting: true,
    enableFilter: true,
    filterType: 'select',
  },
  {
    id: 'alias',
    accessor: 'alias',
    header: 'pages.operations.devices.columns.alias',
    enableSorting: true,
    enableFilter: true,
    filterType: 'select',
  },
  {
    id: 'type',
    accessor: 'type',
    header: 'pages.operations.devices.columns.type',
    enableSorting: true,
    enableFilter: true,
    filterType: 'select',
    cell: DeviceDashboardVehicleTypeCell,
    getOptionLabel: (option: string) => {
      const currentLanguage = i18n.language === 'es' ? 'es' : 'en';
      return formatVehicleTypeForDisplay(option, currentLanguage as 'en' | 'es');
    },
  },
  {
    id: 'status',
    accessor: 'status',
    header: 'pages.operations.devices.columns.status',
    enableSorting: true,
    enableFilter: true,
    filterType: 'select',
    cell: DeviceDashboardStatusCell,
    getOptionLabel: (option: string) => t(`status.${option.toLowerCase()}`),
  },
  /*
  {
    id: 'connectionStatus',
    accessor: 'connectionStatus',
    header: 'pages.operations.devices.columns.connectionStatus',
    enableSorting: true,
    enableFilter: true,
    filterType: 'select',
    cell: DeviceOperationsConnectionCell,
  },
  */
  {
    id: 'operationCount',
    accessor: 'operationCount',
    header: 'pages.operations.devices.columns.operationCount',
    enableSorting: true,
    enableFilter: false,
    cell: ({ getValue }: CellContext<DeviceDashboardTableData, number>) => {
      const value = getValue();
      return <span className="font-medium text-gray-900">{value || 0}</span>;
    },
  },
  /*
  {
    id: 'effectiveness',
    accessor: 'effectiveness',
    header: 'pages.operations.devices.columns.effectiveness',
    enableSorting: true,
    enableFilter: false,
    cell: DeviceDashboardEffectivenessCell,
  },
  */
  {
    id: 'lastConnection',
    accessor: 'lastConnection',
    header: 'pages.operations.devices.columns.lastConnection',
    enableSorting: true,
    enableFilter: false,
    cell: DeviceDashboardLastActivityCell,
  },
  {
    id: 'lastConnectionTz',
    accessor: 'lastConnectionTz',
    header: 'pages.operations.devices.columns.lastConnectionZone',
    enableSorting: false,
    enableFilter: false,
    cell: DeviceDashboardLastConnectionTzCell,
  },
  {
    id: 'lastOperation',
    accessor: 'lastOperation',
    header: 'pages.operations.devices.columns.lastOperation',
    enableSorting: true,
    enableFilter: false,
    cell: DeviceDashboardLastOperationCell,
  },
  {
    id: 'lastOperationTz',
    accessor: 'lastOperationTz',
    header: 'pages.operations.devices.columns.lastOperationZone',
    enableSorting: false,
    enableFilter: false,
    cell: DeviceDashboardLastOperationTzCell,
  },
];
